import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../contexts/ThemeContext';
import { bookingsAPI } from '../../../services/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { TrendingUp, Users, CheckCircle, XCircle, Clock, CalendarDays } from 'lucide-react';
import toast from 'react-hot-toast';

const BookingAnalytics = () => {
    const { t } = useTranslation();
    const { isDarkMode } = useTheme();

    const [stats, setStats] = useState(null);
    const [trends, setTrends] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filter states
    const [period, setPeriod] = useState('30'); // days

    useEffect(() => {
        fetchAnalytics();
    }, [period]);

    const fetchAnalytics = async () => {
        try {
            setLoading(true);
            const endDate = new Date();
            const startDate = new Date();
            startDate.setDate(endDate.getDate() - parseInt(period));

            const res = await bookingsAPI.getStats({
                startDate: startDate.toISOString(),
                endDate: endDate.toISOString()
            });

            setStats(res.stats);
            setTrends(res.trends || []);
        } catch (error) {
            console.error('Error fetching analytics:', error);
            toast.error(t('error_loading_data'));
        } finally {
            setLoading(false);
        }
    };

    if (loading || !stats) {
        return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div></div>;
    }

    const StatCard = ({ title, value, subtitle, icon, colorClass }) => (
        <div className={`p-6 rounded-xl border ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'}`}>
            <div className="flex items-start justify-between">
                <div>
                    <p className={`text-sm font-medium ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>{title}</p>
                    <h3 className={`text-2xl font-bold mt-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{value}</h3>
                    {subtitle && <p className={`text-xs mt-2 font-medium ${isDarkMode ? 'text-zinc-500' : 'text-gray-400'}`}>{subtitle}</p>}
                </div>
                <div className={`p-3 rounded-xl ${colorClass}`}>
                    {icon}
                </div>
            </div>
        </div>
    );

    const pieData = [
        { name: t('converted') || 'Converted', value: stats.converted, color: '#10b981' },
        { name: t('active_holds') || 'Active Holds', value: stats.active, color: '#3b82f6' },
        { name: t('cancelled') || 'Cancelled', value: stats.cancelled, color: '#ef4444' },
        { name: t('expired') || 'Expired', value: stats.expired, color: '#6b7280' },
    ].filter(item => item.value > 0);

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className={`text-2xl font-bold flex items-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        <TrendingUp className="mr-3 text-red-500" />
                        {t('booking_analytics')}
                    </h1>
                    <p className={`mt-1 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {t('track_conversion_and_holds')}
                    </p>
                </div>

                <select
                    value={period}
                    onChange={(e) => setPeriod(e.target.value)}
                    className={`px-4 py-2 text-sm rounded-lg border font-medium focus:outline-none focus:ring-2 focus:ring-red-500 ${isDarkMode ? 'bg-zinc-900 border-zinc-800 text-white' : 'bg-white border-gray-200 text-gray-700'}`}
                >
                    <option value="7">{t('last_7_days')}</option>
                    <option value="30">{t('last_30_days')}</option>
                    <option value="90">{t('last_90_days')}</option>
                    <option value="365">{t('last_year')}</option>
                </select>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard
                    title={t('total_bookings')}
                    value={stats.total}
                    subtitle={t('in_selected_period')}
                    icon={<CalendarDays size={24} className={isDarkMode ? 'text-gray-400' : 'text-gray-600'} />}
                    colorClass={isDarkMode ? 'bg-zinc-800' : 'bg-gray-100'}
                />
                <StatCard
                    title={t('conversion_rate')}
                    value={`${stats.conversionRate}%`}
                    subtitle={t('converted_to_tenants', { count: stats.converted })}
                    icon={<CheckCircle size={24} className="text-green-500" />}
                    colorClass={isDarkMode ? 'bg-green-900/20' : 'bg-green-50'}
                />
                <StatCard
                    title={t('cancellation_rate')}
                    value={`${stats.cancellationRate}%`}
                    subtitle={t('cancelled_holds', { count: stats.cancelled })}
                    icon={<XCircle size={24} className="text-red-500" />}
                    colorClass={isDarkMode ? 'bg-red-900/20' : 'bg-red-50'}
                />
                <StatCard
                    title={t('avg_hold_time')}
                    value={`${stats.avgHoldHours}h`}
                    subtitle={t('before_conversion_cancel')}
                    icon={<Clock size={24} className="text-blue-500" />}
                    colorClass={isDarkMode ? 'bg-blue-900/20' : 'bg-blue-50'}
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Trends Chart */}
                <div className={`p-6 rounded-xl border lg:col-span-2 ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'}`}>
                    <h3 className={`text-lg font-bold mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{t('volume_trends')}</h3>
                    <div className="h-72">
                        {trends.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={trends} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isDarkMode ? '#3f3f46' : '#e5e7eb'} />
                                    <XAxis
                                        dataKey="date"
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: isDarkMode ? '#a1a1aa' : '#6b7280', fontSize: 12 }}
                                        tickFormatter={(val) => new Date(val).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                                    />
                                    <YAxis axisLine={false} tickLine={false} tick={{ fill: isDarkMode ? '#a1a1aa' : '#6b7280', fontSize: 12 }} allowDecimals={false} />
                                    <RechartsTooltip
                                        contentStyle={{ backgroundColor: isDarkMode ? '#18181b' : '#fff', borderColor: isDarkMode ? '#27272a' : '#e5e7eb', color: isDarkMode ? '#fff' : '#000', borderRadius: '8px' }}
                                        labelFormatter={(val) => new Date(val).toLocaleDateString()}
                                    />
                                    <Bar dataKey="total" name={t('total_holds')} fill="#dc2626" radius={[4, 4, 0, 0]} />
                                    <Bar dataKey="converted" name={t('converted')} fill="#10b981" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="h-full flex items-center justify-center text-sm text-gray-500">{t('no_trend_data')}</div>
                        )}
                    </div>
                </div>

                {/* Status Breakdown Pie */}
                <div className={`p-6 rounded-xl border ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'}`}>
                    <h3 className={`text-lg font-bold mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{t('status_breakdown')}</h3>
                    <div className="h-56">
                        {pieData.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={pieData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={60}
                                        outerRadius={80}
                                        paddingAngle={5}
                                        dataKey="value"
                                    >
                                        {pieData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <RechartsTooltip
                                        contentStyle={{ backgroundColor: isDarkMode ? '#18181b' : '#fff', borderColor: isDarkMode ? '#27272a' : '#e5e7eb', borderRadius: '8px', color: isDarkMode ? '#fff' : '#000' }}
                                        itemStyle={{ color: isDarkMode ? '#fff' : '#000' }}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="h-full flex items-center justify-center text-sm text-gray-500">{t('no_status_data')}</div>
                        )}
                    </div>

                    <div className="mt-4 space-y-2">
                        {pieData.map((item, i) => (
                            <div key={i} className="flex justify-between items-center text-sm">
                                <span className={`flex items-center gap-2 ${isDarkMode ? 'text-zinc-300' : 'text-gray-600'}`}>
                                    <span className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></span>
                                    {item.name}
                                </span>
                                <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{item.value}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BookingAnalytics;
